import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useUserInfo } from "../../../../context/user";
import {
  useWebhookConfigs,
  useCreateWebhookConfig,
  useToggleWebhookConfig,
  useDeleteWebhookConfig,
  useWebhookConfigSecret,
} from "../../../../queries/webhooks";
import CreateWebhookModal from "../../../../components/CreateWebhookModal";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Switch } from "components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import {
  Webhook,
  Eye,
  Copy,
  Check,
  Trash2,
  X,
  Plus,
  Loader2,
} from "lucide-react";

const formatCreatedDate = (dateInput) => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
    } catch (error) {
      // Clipboard may be unavailable; ignore silently.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={handleCopy}
      aria-label="Copy"
      className={copied ? "text-[hsl(var(--success))]" : ""}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
};

function Webhooks() {
  const userInfo = useUserInfo();
  const user = userInfo[0];
  const userActiveLocation = userInfo[4];
  const user_id = user?._id;
  const account_id = userActiveLocation?.id;

  const [errorMsg, setErrorMsg] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");

  const [detailsId, setDetailsId] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  const { data: webhooks = [], isLoading: loading } = useWebhookConfigs(user_id, account_id);
  const createWebhook = useCreateWebhookConfig(user_id, account_id);
  const toggleWebhook = useToggleWebhookConfig(user_id, account_id);
  const deleteWebhookMutation = useDeleteWebhookConfig(user_id, account_id);
  const fetchSecret = useWebhookConfigSecret(user_id);

  const detailsWebhook = useMemo(
    () => webhooks.find((hook) => hook.source_id === detailsId) || null,
    [webhooks, detailsId]
  );
  const deleteWebhook = useMemo(
    () => webhooks.find((hook) => hook.source_id === deleteId) || null,
    [webhooks, deleteId]
  );

  const closeCreate = () => {
    setShowCreate(false);
    setNewName("");
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || !user_id) return;
    try {
      const created = await createWebhook.mutateAsync(trimmed);
      if (created) {
        setDetailsData({
          source_id: created.source_id,
          webhook_name: created.webhook_name,
          source_url: created.source_url,
          secret_key: created.secret_key,
          active: created.active,
        });
        setDetailsId(created.source_id);
        setShowSecret(false);
      }
    } catch (error) {
      // Shown via toast (not the page banner): this fires while the create
      // modal is still open, and the banner renders behind the modal overlay.
      toast.error(error?.message || "Failed to create webhook");
      return;
    }
    closeCreate();
  };

  const handleToggleActive = async (source_id, value) => {
    if (!user_id) return;
    try {
      await toggleWebhook.mutateAsync({ source_id, active: value });
    } catch (error) {
      setErrorMsg(error?.message || "Failed to toggle webhook");
    }
  };

  const openDetails = async (source_id) => {
    if (!user_id) return;
    setShowSecret(false);
    setDetailsId(source_id);
    setDetailsData(null);
    try {
      const data = await fetchSecret.mutateAsync(source_id);
      setDetailsData(data || null);
    } catch (error) {
      setErrorMsg(error?.message || "Failed to load webhook details");
      setDetailsId(null);
    }
  };

  const closeDetails = () => {
    setDetailsId(null);
    setDetailsData(null);
    setShowSecret(false);
  };

  const confirmDelete = async () => {
    if (!deleteId || !user_id) return;
    try {
      await deleteWebhookMutation.mutateAsync(deleteId);
    } catch (error) {
      // Shown via toast (not the page banner): this fires while the delete
      // confirmation modal is still open, and the banner renders behind the
      // modal overlay.
      const linkedTasks = error?.data?.count;
      if (error?.code === "TASKS_LINKED" && linkedTasks) {
        toast.error(
          `Cannot delete: ${linkedTasks} task(s) are using this webhook. Remove the tasks first.`
        );
      } else {
        toast.error(error?.message || "Failed to delete webhook");
      }
      return;
    }
    setDeleteId(null);
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
          <span className="page-eyebrow">Configure</span>
          <h1 className="page-display">
            Webhooks.{" "}
            <span className="accent block md:inline">Events from the outside in.</span>
          </h1>
          <p className="page-lede">
            Webhooks let external tools send events into your account.
          </p>
        </div>
        <Button type="button" onClick={() => setShowCreate(true)} className="shrink-0">
          <Plus className="h-4 w-4" />
          Create webhook
        </Button>
      </div>

      {errorMsg && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <span>{errorMsg}</span>
          <button
            type="button"
            onClick={() => setErrorMsg("")}
            className="text-destructive/70 hover:text-destructive"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex flex-col gap-3">
        {loading ? (
          <div className="rounded-lg border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
            Loading webhooks…
          </div>
        ) : webhooks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
            No webhooks yet. Create one to start receiving events.
          </div>
        ) : (
          webhooks.map((hook) => (
            <div
              key={hook.source_id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-4"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Webhook className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-semibold text-foreground">
                    {hook.webhook_name}
                  </div>
                  <div className="text-[13px] text-muted-foreground">
                    Created {formatCreatedDate(hook.configured_at)}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-3">
                {hook.managed ? (
                
                  <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                    Managed
                  </span>
                ) : (
                  <>
                    <span
                      className={`text-sm ${
                        hook.active ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {hook.active ? "Active" : "Inactive"}
                    </span>
                    <Switch
                      checked={hook.active}
                      disabled={
                        toggleWebhook.isPending &&
                        toggleWebhook.variables?.source_id === hook.source_id
                      }
                      onCheckedChange={(value) =>
                        handleToggleActive(hook.source_id, value)
                      }
                      aria-label={
                        hook.active ? "Deactivate webhook" : "Activate webhook"
                      }
                    />
                  </>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openDetails(hook.source_id)}
                >
                  <Eye className="h-4 w-4" />
                  View details
                </Button>
                {!hook.managed && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Delete webhook"
                    onClick={() => setDeleteId(hook.source_id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create webhook modal (shared with Tasks form) */}
      <CreateWebhookModal
        open={showCreate}
        name={newName}
        onNameChange={setNewName}
        creating={createWebhook.isPending}
        onCreate={handleCreate}
        onClose={closeCreate}
      />

      {/* Webhook details */}
      <Dialog
        open={!!detailsId}
        onOpenChange={(open) => !open && closeDetails()}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {detailsData?.webhook_name || detailsWebhook?.webhook_name || "Webhook"}
            </DialogTitle>
            <DialogDescription>
              Use this URL in the service that will send data to your account.
            </DialogDescription>
          </DialogHeader>

          {fetchSecret.isPending ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-[13px] font-medium text-muted-foreground">
                Webhook URL
              </label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={detailsData?.source_url || ""}
                  className="bg-muted font-mono text-sm"
                />
                <CopyButton value={detailsData?.source_url} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" onClick={closeDetails}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteWebhook}
        onOpenChange={(open) => {
          if (!open && !deleteWebhookMutation.isPending) setDeleteId(null);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Delete webhook?</DialogTitle>
            <DialogDescription>
              This will stop events from being sent to{" "}
              <span className="font-semibold text-foreground">
                &quot;{deleteWebhook?.webhook_name}&quot;
              </span>
              . This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteId(null)}
              disabled={deleteWebhookMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteWebhookMutation.isPending}
            >
              {deleteWebhookMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Webhooks;
